import { useToast } from '../contexts/ToastContext';
import { useConfirmation } from './ConfirmDialog';
import './FacilityDocuments.css';
import React, { FormEvent, useCallback, useEffect, useState, useRef } from 'react';
import { DocumentCategory, FacilityDocument, WrmpPlan } from '../types';
import { facilityDocumentsService as service } from '../services/facilityDocuments';
import { saveWrmpFile, wrmpService } from '../services/wrmp';

const categories: DocumentCategory[] = ['diagram', 'photo', 'certificate', 'report', 'procedure', 'other'];
const label = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

function Thumbnail({ facilityId, item }: { facilityId: string; item: FacilityDocument }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let active = true;
    let objectUrl = '';
    service.download(facilityId, item.id).then(blob => {
      if (active) { objectUrl = URL.createObjectURL(blob); setUrl(objectUrl); }
    }).catch(() => {});
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [facilityId, item.id]);
  return url ? <img src={url} alt={item.title} style={{ width: 180, height: 130, objectFit: 'contain' }} /> : <span>Preview unavailable</span>;
}

export default function FacilityDocuments({ facilityId, canEdit = false, onOpenPlan }: { facilityId: string; canEdit?: boolean; onOpenPlan?: () => void }) {
  const { showSuccess } = useToast();
  const { confirm, dialog } = useConfirmation();
  const fileInput = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<FacilityDocument[]>([]);
  const [plan, setPlan] = useState<WrmpPlan | null>(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [category, setCategory] = useState<DocumentCategory>(canEdit ? 'other' : 'photo');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File>();
  const [editing, setEditing] = useState<FacilityDocument | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => service.list(facilityId).then(setItems), [facilityId]);
  useEffect(() => {
    load().catch(() => setError('Could not load documents'));
    wrmpService.getPlan(facilityId).then(setPlan).catch(() => setError('Could not load flow-diagram history'));
  }, [facilityId, load]);
  const run = async (operation: () => Promise<unknown>) => {
    setBusy(true); setError('');
    try { await operation(); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not update documents'); }
    finally { setBusy(false); }
  };
  const upload = (event: FormEvent) => {
    event.preventDefault();
    if (file) run(async () => { await service.upload(facilityId, file, category, title.trim()); setTitle(''); setFile(undefined); if (fileInput.current) fileInput.current.value = ''; showSuccess('Document uploaded'); });
  };
  const download = async (item: FacilityDocument) => {
    try {
      const blob = await service.download(facilityId, item.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = item.filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setError('Could not download document'); }
  };
  const visible = items.filter(item => !filter || item.category === filter);
  const card = (item: FacilityDocument) => <article key={item.id} style={{ padding: 12, border: '1px solid #ddd', borderRadius: 8 }}>
    {item.content_type.startsWith('image/') && <Thumbnail facilityId={facilityId} item={item} />}
    <h4>{item.title}</h4><p>{item.filename} · {label(item.category)}{!item.include_in_reports && ' · Excluded from reports'}</p>
    <button className="btn btn-secondary" onClick={() => download(item)}>Download {item.title}</button>
    {canEdit && <>
      <button className="btn btn-secondary" disabled={busy} onClick={() => setEditing(item)}>Edit {item.title}</button>
      <button className="btn btn-secondary" disabled={busy} onClick={async () => { if (await confirm(`Delete "${item.title}"?`)) run(() => service.delete(facilityId, item.id)); }}>Delete {item.title}</button>
    </>}
  </article>;
  return <section className="facility-documents" aria-label="Documents and images">
    {dialog}<h3>Documents &amp; images</h3>
    {error && <p role="alert">{error}</p>}
    <label>Filter by category<select value={filter} onChange={e => setFilter(e.target.value)}><option value="">All categories</option>{categories.map(c => <option key={c} value={c}>{label(c)}</option>)}</select></label>
    <form className="document-upload" onSubmit={upload}>
      <label>Document title<input required maxLength={255} value={title} onChange={e => setTitle(e.target.value)} /></label>
      <label>Upload category<select value={category} onChange={e => setCategory(e.target.value as DocumentCategory)}>{(canEdit ? categories : ['photo'] as DocumentCategory[]).map(c => <option key={c} value={c}>{label(c)}</option>)}</select></label>
      <label>File<input ref={fileInput} type="file" required accept={canEdit ? '.png,.jpg,.jpeg,.gif,.tif,.tiff,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv' : '.png,.jpg,.jpeg,.gif,.tif,.tiff,.webp'} onChange={e => setFile(e.target.files?.[0])} /></label>
      <p>{canEdit ? 'Images, PDF, Office documents, text or CSV.' : 'Images only (PNG, JPEG, GIF, TIFF or WebP).'} Maximum size is set by your administrator (normally 25 MB).</p>
      <button className="btn btn-primary" disabled={busy || !file || !title.trim()}>Upload</button>
    </form>
    {editing && <form onSubmit={e => { e.preventDefault(); run(async () => { await service.update(facilityId, editing.id, { title: editing.title, category: editing.category, include_in_reports: editing.include_in_reports }); setEditing(null); }); }}>
      <label>Edit title<input required value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} /></label>
      <label>Edit category<select value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value as DocumentCategory })}>{categories.map(c => <option key={c} value={c}>{label(c)}</option>)}</select></label>
      <label><input type="checkbox" checked={editing.include_in_reports} onChange={e => setEditing({ ...editing, include_in_reports: e.target.checked })} />Include in reports</label>
      <button className="btn btn-primary" disabled={busy}>Save</button><button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
    </form>}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>{visible.filter(i => i.content_type.startsWith('image/')).map(card)}</div>
    <div>{visible.filter(i => !i.content_type.startsWith('image/')).map(card)}</div>
    {!visible.length && <p>No documents in this category.</p>}
    <h4>WRMP flow-diagram versions</h4>
    {plan?.files.filter(f => f.kind === 'flow_diagram').map(f => <p key={f.id}>{f.filename} — {new Date(f.created_at).toLocaleDateString('en-AU')} <button className="btn btn-secondary" onClick={() => saveWrmpFile(plan.id, f).catch(() => setError('Could not download flow diagram'))}>Download {f.filename}</button></p>)}
    {onOpenPlan ? <button className="btn btn-secondary" onClick={onOpenPlan}>Open Risk Management Plan</button> : <a href={`/portal/wrmp/${facilityId}`}>Open Risk Management Plan</a>}
  </section>;
}
